import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

const US_STATES = [
  ['','-- Select --'],['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],
  ['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],
  ['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
  ['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],
  ['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],
  ['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],
  ['SC','South Carolina'],['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming']
];

// Cash Back or Low Interest Calculator — mirrors reference image
export default function CashBackVsLowInterestCalc() {
  const [cashBack, setCashBack] = useState(1000);
  const [highRate, setHighRate] = useState(5);
  const [lowRate, setLowRate] = useState(2);
  const [autoPrice, setAutoPrice] = useState(50000);
  const [term, setTerm] = useState(60);
  const [down, setDown] = useState(10000);
  const [tradeIn, setTradeIn] = useState(0);
  const [state, setState] = useState('');
  const [salesTax, setSalesTax] = useState(7);
  const [fees, setFees] = useState(2000);
  const [includeFees, setIncludeFees] = useState(false);

  const c = useMemo(() => {
    // Sale tax on taxable amount (price - trade-in)
    const tax = Math.max(0, (autoPrice - tradeIn)) * salesTax / 100;
    const upfront = down + (includeFees ? 0 : fees);

    function scenario(cashRebate, rate) {
      const priceAfterRebate = autoPrice - cashRebate;
      const feeInLoan = includeFees ? fees : 0;
      const loanAmt = Math.max(0, priceAfterRebate - down - tradeIn + tax + feeInLoan);
      const r = rate / 100 / 12;
      const n = term;
      const M = r === 0 ? loanAmt / n : (loanAmt * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      const totalPay = M * n;
      const interest = totalPay - loanAmt;
      const totalCost = totalPay + upfront + (includeFees ? 0 : 0); // upfront already includes fees when not in loan
      // For consistency with reference: totalCost = price + tax + fees + interest - trade-in - rebate applied
      const totalCostAlt = autoPrice + tax + fees + interest - tradeIn - cashRebate;
      return { loanAmt, tax, upfront, M, totalPay, interest, totalCost: totalCostAlt };
    }

    const cash = scenario(cashBack, highRate);
    const low  = scenario(0, lowRate);
    const savings = cash.totalCost - low.totalCost; // positive => low is better
    const better = savings > 0 ? 'low' : 'cash';
    return { cash, low, savings: Math.abs(savings), better };
  }, [cashBack, highRate, lowRate, autoPrice, term, down, tradeIn, salesTax, fees, includeFees]);

  return (
    <CalcShell look="cnet" title="Cash Back or Low Interest Calculator"
      subtitle="Compare a cash-back rebate against a low-interest rate auto loan."
      about={<>
        <p>Auto manufacturers may offer either a cash back rebate or a low interest rate. This calculator compares the total cost of both offers under the same auto loan and finds which saves you more money.</p>
      </>}
      slug="cash-back"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Inputs */}
        <div>
          <div className="mb-3 mt-1">
            <div className="bg-blue-800 text-white px-3 py-1.5 text-xs font-semibold rounded-t">Cash Back Offer</div>
            <div className="border border-slate-200 border-t-0 rounded-b p-3 space-y-2">
              <div className="field"><label>Cash Back Amount</label>
                <div className="flex gap-2"><span className="self-center text-slate-500">$</span>
                  <input type="number" value={cashBack} onChange={e=>setCashBack(+e.target.value||0)} className="flex-1" data-testid="cb-cash"/></div>
              </div>
              <div className="field"><label>Interest Rate (High)</label>
                <div className="flex gap-2"><input type="number" step="0.01" value={highRate} onChange={e=>setHighRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <div className="bg-blue-800 text-white px-3 py-1.5 text-xs font-semibold rounded-t">Low Interest Rate Offer</div>
            <div className="border border-slate-200 border-t-0 rounded-b p-3 space-y-2">
              <div className="field"><label>Interest Rate (Low)</label>
                <div className="flex gap-2"><input type="number" step="0.01" value={lowRate} onChange={e=>setLowRate(+e.target.value||0)} className="flex-1" data-testid="cb-low"/><span className="self-center text-slate-500">%</span></div>
              </div>
            </div>
          </div>

          <div>
            <div className="bg-blue-800 text-white px-3 py-1.5 text-xs font-semibold rounded-t">Other Information</div>
            <div className="border border-slate-200 border-t-0 rounded-b p-3 space-y-2">
              <div className="field"><label>Auto Price</label>
                <div className="flex gap-2"><span className="self-center text-slate-500">$</span>
                  <input type="number" value={autoPrice} onChange={e=>setAutoPrice(+e.target.value||0)} className="flex-1"/></div>
              </div>
              <div className="field"><label>Loan Term</label>
                <div className="flex gap-2"><input type="number" value={term} onChange={e=>setTerm(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">months</span></div>
              </div>
              <div className="field"><label>Down Payment</label>
                <div className="flex gap-2"><span className="self-center text-slate-500">$</span>
                  <input type="number" value={down} onChange={e=>setDown(+e.target.value||0)} className="flex-1"/></div>
              </div>
              <div className="field"><label>Trade-in Value</label>
                <div className="flex gap-2"><span className="self-center text-slate-500">$</span>
                  <input type="number" value={tradeIn} onChange={e=>setTradeIn(+e.target.value||0)} className="flex-1"/></div>
              </div>
              <div className="field"><label>Your State</label>
                <select value={state} onChange={e=>setState(e.target.value)}>
                  {US_STATES.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="field"><label>Sales Tax</label>
                <div className="flex gap-2"><input type="number" step="0.01" value={salesTax} onChange={e=>setSalesTax(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div>
              </div>
              <div className="field"><label>Title, Registration and Other Fees</label>
                <div className="flex gap-2"><span className="self-center text-slate-500">$</span>
                  <input type="number" value={fees} onChange={e=>setFees(+e.target.value||0)} className="flex-1"/></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={includeFees} onChange={e=>setIncludeFees(e.target.checked)}/> Include All Fees in Loan
              </label>
            </div>
          </div>
        </div>

        {/* Results */}
        <div>
          <div className="result-panel">
            <div className="result-title">Results</div>
            <div className="text-center py-3">
              <div className="text-lg font-bold" style={{color: c.better === 'low' ? '#dc2626' : '#059669'}} data-testid="cb-verdict">
                The {c.better === 'low' ? 'Low Interest Rate Offer' : 'Cash Back Offer'} is Better!
              </div>
              <div className="text-sm text-slate-700 mt-1">
                {c.better === 'low'
                  ? `The low rate will save you ${fmt(c.savings)} in interest, which is larger than the cash back of ${fmt(cashBack)}.`
                  : `The cash back of ${fmt(cashBack)} exceeds the interest savings of ${fmt(c.savings)}.`}
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            <ResultCard title="With Cash Back Offer" data={c.cash} rate={highRate} term={term} highlight={c.better==='cash'} />
            <ResultCard title="With Low Interest Rate Offer" data={c.low} rate={lowRate} term={term} highlight={c.better==='low'} />
          </div>
        </div>
      </div>
    </CalcShell>
  );
}

function ResultCard({ title, data, rate, term, highlight }) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className={`px-3 py-1.5 text-white text-sm font-semibold ${highlight?'bg-emerald-700':'bg-sky-700'}`}>{title}</div>
      <table className="w-full text-sm">
        <tbody>
          <tr><td className="px-3 py-1.5">Total Loan Amount</td><td className="text-right px-3">{fmt(data.loanAmt)}</td></tr>
          <tr><td className="px-3 py-1.5">Sale Tax</td><td className="text-right px-3">{fmt(data.tax)}</td></tr>
          <tr><td className="px-3 py-1.5">Upfront Payment</td><td className="text-right px-3">{fmt(data.upfront)}</td></tr>
          <tr className="bg-slate-50 font-semibold"><td className="px-3 py-1.5">Monthly Pay</td><td className="text-right px-3 text-blue-800">{fmt(data.M)}</td></tr>
          <tr><td className="px-3 py-1.5">Total of {term} Loan Payments</td><td className="text-right px-3">{fmt(data.totalPay)}</td></tr>
          <tr><td className="px-3 py-1.5">Total Loan Interest</td><td className="text-right px-3">{fmt(data.interest)}</td></tr>
          <tr className="border-t"><td className="px-3 py-1.5 font-semibold">Total Cost (price, interest, tax, fees)</td><td className="text-right px-3 font-semibold">{fmt(data.totalCost)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}
